/**
 * chatStore — session UI state + PER-SESSION live stream buffers + the
 * diagrams each session has drawn.
 *
 * Completed turns are server state (useSession); this store holds ONLY:
 *  - which session is active (the one whose route is mounted),
 *  - `live`: one stream buffer PER SESSION ID (several sessions may stream
 *    concurrently; each keeps its own frames/error plus the optimistic
 *    user echo). Buffers survive session switches — the session list's
 *    status glyphs and the transcript's catch-up render read them. Eviction
 *    rides the replay-guard LRU (streamController).
 *  - `diagrams`: per-session ordered diagram list (DECISIONS #62) derived
 *    from replayed history + live `diagram` frames; the newest is the board
 *    the pane draws (DECISIONS #96).
 *  - `paneView`: which of the pane's two views is up, the board or the
 *    infrastructure map (DECISIONS #94/#96). A new drawing for the mounted
 *    session brings the board back.
 *
 * `ingestFrame(sid, frame)` is the single entry point for live frames: it
 * buffers the frame for THAT session unconditionally. Persisted history
 * (`ChatMessage.frames`) is RAW display_json — replay narrows structurally
 * and skips unknown frame types silently (api.md §15).
 */

import { create } from 'zustand';

import type { ChatFrame, ChatMessage, Ulid } from '../api/types';
import type { MentionCandidate } from '../features/chat/mentions';
import {
  asDiagramData,
  diagramsFromMessages,
  type SessionDiagram,
} from '../features/chat/transcript';
import { frameData, frameType } from '../lib/frames';

/** The pane's two views (DECISIONS #96): the board, or the product as it runs. */
export type PaneView = 'board' | 'runtime';

/** One session's in-flight (or just-finished) stream buffer. */
export interface SessionLive {
  /** true between stream start and terminal frame */
  streaming: boolean;
  /** message id of the in-flight assistant turn (from message.start) */
  streamingMessageId: Ulid | null;
  /**
   * Live frame buffer for the in-flight turn, in wire order. This IS the
   * turn: prose, thinking, tool chips and diagrams all render from it
   * (buildTurnBlocks) — no side-channel text accumulation exists any more,
   * because nothing read one and a second store write per delta was pure
   * streaming overhead.
   */
  frames: ChatFrame[];
  /** stream-level error message (error frame or transport failure) */
  error: string | null;
  /** optimistic user turn (rendered until the session refetch includes it) */
  pendingUserText: string | null;
  /** wall clock (ms since epoch) the stream began, for the live elapsed */
  turnStartedAt: number | null;
  /**
   * Wall clock (ms since epoch) of this turn's FIRST `tool.start`, and of
   * its last `tool.result` (or of `message.done`, when a tool never
   * reported back). The pair is the turn's tool time — the elapsed the
   * transcript shows at the end of an activity run (round 3). Client-side
   * by necessity: frames carry no timestamps, so a turn this browser did
   * not watch has no honest number and shows none.
   */
  toolStartedAt: number | null;
  toolEndedAt: number | null;
}

export const EMPTY_LIVE: SessionLive = Object.freeze({
  streaming: false,
  streamingMessageId: null,
  frames: [],
  error: null,
  pendingUserText: null,
  turnStartedAt: null,
  toolStartedAt: null,
  toolEndedAt: null,
});

const EMPTY_DIAGRAMS: readonly SessionDiagram[] = Object.freeze([]);

/**
 * What one session's diagram fold is computed FROM: the persisted history the
 * pane last saw. Kept so a refetch can re-fold without yanking the reader off
 * the diagram they are looking at.
 */
interface DiagramSource {
  messages: ChatMessage[];
}

/**
 * Ordered, de-duplicated: the persisted fold first, then anything the live
 * stream drew that history has not caught up with. A diagram the refetch has
 * since persisted appears once, in its history position.
 */
function mergeDiagrams(
  persisted: readonly SessionDiagram[],
  live: readonly SessionDiagram[],
): SessionDiagram[] {
  const seen = new Set(persisted.map((d) => d.diagram_id));
  return [...persisted, ...live.filter((d) => !seen.has(d.diagram_id))];
}

/** The same diagrams, in the same order (identity is what the pane reacts to). */
function sameDiagrams(a: readonly SessionDiagram[], b: readonly SessionDiagram[]): boolean {
  return a.length === b.length && a.every((d, i) => d.diagram_id === b[i]?.diagram_id);
}

/**
 * Fold one session's diagrams. A read that brought a NEW drawing takes the
 * mounted pane back to the board (DECISIONS #94): the answer drew, and the
 * drawing is what the reader asked for. A poll that changed nothing leaves
 * whichever view is up alone.
 */
function foldDiagrams(
  state: ChatStoreState,
  sessionId: Ulid,
  source: DiagramSource,
): Pick<ChatStoreState, 'diagrams'> & { paneView?: PaneView } {
  const persisted = diagramsFromMessages(source.messages);
  const live = state.liveDiagrams[sessionId] ?? [];
  const folded = mergeDiagrams(persisted, live);
  // A poll that changed nothing must not hand the pane a fresh array: that
  // identity is what re-runs the hotspot decoration, and re-decorating drops
  // the keyboard out of whichever part it had focused.
  const previous = state.diagrams[sessionId];
  const merged = previous !== undefined && sameDiagrams(previous, folded) ? previous : folded;
  const grew = previous === undefined ? merged.length > 0 : merged.length > previous.length;

  return {
    diagrams: { ...state.diagrams, [sessionId]: merged },
    // The pane is the MOUNTED session's: only its own new drawing may move
    // the view; a background session's poll must not repaint what is on screen.
    ...(sessionId === state.activeSessionId && grew ? { paneView: 'board' as const } : {}),
  };
}

/** That session's live buffer, defaulted — never returns undefined. */
export function liveFor(
  state: Pick<ChatStoreState, 'live'>,
  sessionId: Ulid | null | undefined,
): SessionLive {
  if (sessionId === null || sessionId === undefined) return EMPTY_LIVE;
  return state.live[sessionId] ?? EMPTY_LIVE;
}

/**
 * The tool time of a live (or just-finished) turn, or null when this turn
 * did no tool work / has not reported any yet.
 */
export function toolElapsedMs(live: SessionLive): number | null {
  if (live.toolStartedAt === null || live.toolEndedAt === null) return null;
  return Math.max(0, live.toolEndedAt - live.toolStartedAt);
}

export interface ChatStoreState {
  activeSessionId: Ulid | null;
  /** per-session live stream buffers (multi-stream) */
  live: Record<Ulid, SessionLive>;
  /**
   * Tool time per ASSISTANT MESSAGE id, kept once the turn is done so the
   * number survives the session refetch that replaces the live buffer with
   * server truth. Only turns this browser watched are in here; a reload (or
   * a replay from history) leaves it empty and the transcript shows nothing.
   */
  turnElapsedMs: Record<Ulid, number>;
  /** per-session ordered diagrams (replayed + live), newest last */
  diagrams: Record<Ulid, SessionDiagram[]>;
  /**
   * Diagrams that arrived on THIS session's live stream, kept apart from the
   * fold above so re-folding history cannot drop the picture a turn just
   * drew (history has not persisted it yet).
   */
  liveDiagrams: Record<Ulid, SessionDiagram[]>;
  /** what the fold above is computed from (see DiagramSource) */
  diagramSource: Record<Ulid, DiagramSource>;
  /**
   * Which of the pane's two views is up for the mounted session: the board
   * (the newest drawing, edited in place by every answer) or the
   * infrastructure map. A new drawing brings the board back; switching
   * sessions starts on it.
   */
  paneView: PaneView;
  /**
   * Sessions whose history has been replayed into the stores — an LRU (~8,
   * oldest first). Lives here (not module scope) so `setActiveSession` —
   * which wipes the session's derived state — also invalidates the
   * incoming session's guard; re-entering a session then replays again.
   * Eviction drops the replay mark AND the evicted session's live buffer.
   */
  replayedSessionIds: Ulid[];
  /**
   * Text staged into the composer (palette ask-guide). SURVIVES
   * `setActiveSession` so a draft staged before the session existed can
   * auto-submit into the fresh session.
   */
  composerDraft: string | null;
  /**
   * The parts picked on the pane's drawing (DECISIONS #88, chips in the
   * composer since #95): the composer shows them as chips beside the
   * question, and the send carries them as the turn's focus. Written by the
   * pane and by the composer's chips; cleared by a send, by Escape on the
   * pane, and when the picture changes.
   */
  diagramSelection: { sessionId: string; diagramId: string; labels: string[] } | null;
  /**
   * A change the pane asks the composer to make to its text (DECISIONS #96;
   * owner: "The @ should be inline like Conductor"): put `@Label` tokens in
   * (every part a gesture added, in one request: a marquee adds several at
   * once and the slot holds one request), or take some out. The composer
   * consumes it and clears it.
   */
  composerRequest:
    | { kind: 'insert'; candidates: readonly MentionCandidate[] }
    | { kind: 'remove'; labels: readonly string[] }
    | null;

  setActiveSession: (sessionId: Ulid | null) => void;
  beginStream: (sessionId: Ulid) => void;
  /** Buffer + dispatch one live frame (the SSE onFrame callback). */
  ingestFrame: (sessionId: Ulid, frame: ChatFrame) => void;
  /** Transport-level failure (terminal `error` frames go through ingestFrame). */
  failStream: (sessionId: Ulid, message: string) => void;
  /** Partial update of one session's live buffer (stream controller plumbing). */
  patchLive: (sessionId: Ulid, patch: Partial<SessionLive>) => void;
  /** Drop one session's live buffer + diagrams (LRU eviction). */
  clearLive: (sessionId: Ulid) => void;
  /**
   * Rebuild the session's diagrams from persisted history
   * (`ChatMessage.frames` = RAW display_json) for the ACTIVE session
   * (retired frame types are skipped: DECISIONS #62).
   */
  replaySession: (messages: ChatMessage[]) => void;
  /**
   * Keep the diagram fold in step with the fetched session WITHOUT touching
   * the live buffer or yanking the reader off the diagram they are looking at.
   * Runs on every session read; `replaySession` runs once.
   */
  syncDiagramSource: (sessionId: Ulid, messages: ChatMessage[]) => void;
  /** The strip's switch (DiagramPane): the board, or the infrastructure map. */
  setPaneView: (view: PaneView) => void;
  setDiagramSelection: (selection: { sessionId: string; diagramId: string; labels: string[] } | null) => void;
  /**
   * Stage text into the composer (the palette's ask-guide, a prepared
   * question from the changes panel). `setComposerDraft(null)` is the
   * composer saying it TOOK the draft.
   */
  setComposerDraft: (text: string | null) => void;
  /** The pane (or a panel) asks the composer to insert or remove a token. */
  requestComposer: (request: ChatStoreState['composerRequest']) => void;
  reset: () => void;
}

const initialState = {
  activeSessionId: null as Ulid | null,
  live: {} as Record<Ulid, SessionLive>,
  turnElapsedMs: {} as Record<Ulid, number>,
  diagrams: {} as Record<Ulid, SessionDiagram[]>,
  liveDiagrams: {} as Record<Ulid, SessionDiagram[]>,
  diagramSource: {} as Record<Ulid, DiagramSource>,
  paneView: 'board' as PaneView,
  replayedSessionIds: [] as Ulid[],
  composerDraft: null as string | null,
  diagramSelection: null as { sessionId: string; diagramId: string; labels: string[] } | null,
  composerRequest: null as ChatStoreState['composerRequest'],
};

export const useChatStore = create<ChatStoreState>((set, get) => ({
  ...initialState,

  setActiveSession(sessionId) {
    // The reset deliberately EXCLUDES composerDraft (a draft staged before
    // the session existed must survive the switch so it can auto-submit),
    // the `live` map (streams keep running across navigation; their buffers
    // are the session list's status truth), and `diagrams` (per-session,
    // like live buffers). The INCOMING session's replay mark is forgotten —
    // its derived state was just wiped, so it must replay again.
    // diagramSelection is deliberately NOT excluded: it points at a drawing
    // on the screen being left behind, so it resets with everything else.
    set((s) => ({
      ...initialState,
      activeSessionId: sessionId,
      composerDraft: s.composerDraft,
      live: s.live,
      // Measured tool time belongs to the turns, not to the viewport: it
      // survives a switch exactly like the buffers.
      turnElapsedMs: s.turnElapsedMs,
      diagrams: s.diagrams,
      liveDiagrams: s.liveDiagrams,
      diagramSource: s.diagramSource,
      replayedSessionIds:
        sessionId === null
          ? s.replayedSessionIds
          : s.replayedSessionIds.filter((id) => id !== sessionId),
    }));
  },

  beginStream(sessionId) {
    set((s) => ({
      live: {
        ...s.live,
        [sessionId]: {
          ...EMPTY_LIVE,
          streaming: true,
          turnStartedAt: Date.now(),
          // The optimistic echo was staged just before the stream began.
          pendingUserText: liveFor(s, sessionId).pendingUserText,
        },
      },
    }));
  },

  ingestFrame(sessionId, frame) {
    const isActive = get().activeSessionId === sessionId;

    const patch = (p: Partial<SessionLive>) => {
      set((s) => ({
        live: { ...s.live, [sessionId]: { ...liveFor(s, sessionId), ...p } },
      }));
    };
    patch({ frames: [...liveFor(get(), sessionId).frames, frame] });

    switch (frameType(frame)) {
      case 'message.start': {
        const id = frameData(frame)?.['message_id'];
        if (typeof id === 'string') patch({ streamingMessageId: id });
        break;
      }
      case 'diagram': {
        const diagram = asDiagramData(frameData(frame));
        if (diagram !== null) {
          set((s) => {
            const list = s.diagrams[sessionId] ?? [];
            if (list.some((d) => d.diagram_id === diagram.diagram_id)) return s;
            const live = s.liveDiagrams[sessionId] ?? [];
            return {
              diagrams: { ...s.diagrams, [sessionId]: [...list, diagram] },
              // Kept apart so a later re-fold cannot drop it.
              liveDiagrams: { ...s.liveDiagrams, [sessionId]: [...live, diagram] },
              // A fresh diagram brings the mounted session's pane back to the board.
              ...(isActive ? { paneView: 'board' as const } : {}),
            };
          });
        }
        break;
      }
      case 'tool.start': {
        // The clock starts at the FIRST tool of the turn and never restarts.
        if (liveFor(get(), sessionId).toolStartedAt === null) {
          patch({ toolStartedAt: Date.now() });
        }
        break;
      }
      case 'tool.result':
        patch({ toolEndedAt: Date.now() });
        break;
      case 'message.done': {
        const live = liveFor(get(), sessionId);
        const started = live.toolStartedAt;
        // A tool that never reported back still bounds the run: the turn
        // ending is the last honest moment we have for it.
        const ended = started !== null && live.toolEndedAt === null ? Date.now() : live.toolEndedAt;
        patch({ streaming: false, toolEndedAt: ended });
        const messageId = frameData(frame)?.['message_id'] ?? live.streamingMessageId;
        if (typeof messageId === 'string' && started !== null && ended !== null) {
          // Keep the number past the refetch that replaces this buffer with
          // the persisted turn (it is the same turn, freshly watched).
          set((s) => ({
            turnElapsedMs: { ...s.turnElapsedMs, [messageId]: Math.max(0, ended - started) },
          }));
        }
        break;
      }
      case 'error': {
        const message = frameData(frame)?.['message'];
        patch({
          streaming: false,
          error: typeof message === 'string' ? message : 'The stream failed.',
        });
        break;
      }
      default:
        // text.delta and thinking.delta land here on purpose: the frame
        // append above is their whole job (one set() per delta — the hot
        // path of a streaming answer), and the transcript renders prose,
        // thinking and chips straight from the frame buffer. Anything
        // unknown is skipped silently (forward compatibility).
        break;
    }
  },

  failStream(sessionId, message) {
    set((s) => ({
      live: {
        ...s.live,
        [sessionId]: { ...liveFor(s, sessionId), streaming: false, error: message },
      },
    }));
  },

  patchLive(sessionId, patch) {
    set((s) => ({
      live: { ...s.live, [sessionId]: { ...liveFor(s, sessionId), ...patch } },
    }));
  },

  clearLive(sessionId) {
    set((s) => {
      if (s.live[sessionId] === undefined && s.diagrams[sessionId] === undefined) return s;
      const live = { ...s.live };
      const diagrams = { ...s.diagrams };
      const liveDiagrams = { ...s.liveDiagrams };
      const diagramSource = { ...s.diagramSource };
      // The tool times of this session's turns go with it: the answers the
      // fold last read, and the one its buffer was streaming.
      const turnElapsedMs = { ...s.turnElapsedMs };
      for (const message of s.diagramSource[sessionId]?.messages ?? []) {
        delete turnElapsedMs[message.id];
      }
      const streamingId = s.live[sessionId]?.streamingMessageId;
      if (streamingId !== null && streamingId !== undefined) delete turnElapsedMs[streamingId];
      delete live[sessionId];
      delete diagrams[sessionId];
      delete liveDiagrams[sessionId];
      delete diagramSource[sessionId];
      return { live, diagrams, liveDiagrams, diagramSource, turnElapsedMs };
    });
  },

  replaySession(messages) {
    const sessionId = get().activeSessionId;
    set((s) => {
      const live = { ...s.live };
      const liveDiagrams = { ...s.liveDiagrams };
      // Persisted history supersedes any stale finished buffer of this session
      // — including the diagrams that buffer drew, which history now carries.
      if (sessionId !== null) {
        delete live[sessionId];
        delete liveDiagrams[sessionId];
      }
      return { live, liveDiagrams };
    });
    if (sessionId === null) return;
    get().syncDiagramSource(sessionId, messages);
  },

  syncDiagramSource(sessionId, messages) {
    set((s) => {
      const source: DiagramSource = { messages };
      const next = foldDiagrams(s, sessionId, source);
      return {
        diagramSource: { ...s.diagramSource, [sessionId]: source },
        ...next,
      };
    });
  },

  setPaneView(view) {
    set({ paneView: view });
  },

  setDiagramSelection(selection) {
    set({ diagramSelection: selection });
  },

  setComposerDraft(text) {
    set({ composerDraft: text });
  },

  requestComposer(request) {
    set({ composerRequest: request });
  },

  reset() {
    set({ ...initialState });
  },
}));

/** Hook: one session's live buffer (subscribes to just that entry). */
export function useSessionLive(sessionId: Ulid | null | undefined): SessionLive {
  const stored = useChatStore((s) =>
    sessionId === null || sessionId === undefined ? undefined : s.live[sessionId],
  );
  return stored ?? EMPTY_LIVE;
}

/** Hook: one session's ordered diagram list (replayed + live). */
export function useSessionDiagrams(sessionId: Ulid | null | undefined): readonly SessionDiagram[] {
  const stored = useChatStore((s) =>
    sessionId === null || sessionId === undefined ? undefined : s.diagrams[sessionId],
  );
  return stored ?? EMPTY_DIAGRAMS;
}
